const shippingCourierTypes = ['jne', 'siCepat', 'sap', 'iDexpress', 'jNt', 'lion', 'spx']
const normalizeShippingCourier = (courier) => String(courier || '')
  .replace(/[^a-z0-9]/gi, '')
  .toLowerCase()
const isInactiveShippingCourier = (courier) => [
  'ninja',
  'ninjaxpress',
  'ninjaexpress'
].includes(normalizeShippingCourier(courier))
const shippingCourierTypesCargo = ['JNECargo', 'SiCepatCargo', 'SapCargo', 'iDexpressCargo']
const codShippingFields = [
  'shippingType',
  'shippingServices',
  'additionalCost',
  'flatShippingPrice',
  'shippingDiscount',
  'hideAdditionalCost',
  'originalShippingCost',
  'shippingHideView',
  'courierType',
  'cargoCourierType',
  'hasRegularShipping',
  'hasCargoShipping',
  'hasNoAvailableShippingCourier',
  'ninja'
].concat(shippingCourierTypes, shippingCourierTypesCargo)
const copyShippingValue = (value) => {
  if (Array.isArray(value)) return [...value]
  return value && typeof value === 'object' ? { ...value } : value
}
// Without a separate COD setup, COD orders use the non-COD shipping setup (as the order API
// does), so a shippingCOD left from an earlier COD setup must not price the checkout (FW-4026).
const applyNonCodShippingToCod = (checkoutData) => {
  if (!checkoutData || checkoutData.isPaymentTypeHaveDifferentValue) return checkoutData

  const shippingCOD = { ...(checkoutData.shippingCOD || {}) }
  codShippingFields.forEach(field => {
    shippingCOD[field] = copyShippingValue(checkoutData[field])
  })
  checkoutData.shippingCOD = shippingCOD
  return checkoutData
}
const UNAVAILABLE_SHIPPING_CHECKOUT_ERROR = 'Gagal membuat pesanan, silahkan hubungi seller'
const isUnavailableAutomatedShipping = (checkoutData = {}, payment = '') => {
  const shippingConfig = checkoutData.isPaymentTypeHaveDifferentValue && payment === 'COD'
    ? checkoutData.shippingCOD
    : checkoutData

  return Boolean(
    checkoutData.addShipping &&
    shippingConfig &&
    shippingConfig.shippingType === 'automated' &&
    shippingConfig.hasNoAvailableShippingCourier
  )
}
// Only priced couriers are offered, except that with the coverage check on a courier
// that does not reach the destination stays listed as "Alamat tidak terjangkau".
const isDisplayableShippingEstimate = (estimate, isShippingCoverageCheckEnabled = false) => Boolean(
  estimate && (
    Number(estimate.price) > 0 ||
    (isShippingCoverageCheckEnabled && estimate.unsupported === true)
  )
)
const UNREACHABLE_SHIPPING_ADDRESS_MESSAGE = 'Pengiriman yang kamu pilih belum support COD & Non COD'
const SHIPPING_ESTIMATE_MAX_ATTEMPTS = 3
// Longer than the server's 15s call to Mengantar, so the server answers 503 itself first.
const SHIPPING_ESTIMATE_TIMEOUT = 17000
const SHIPPING_ESTIMATE_RETRY_BASE_DELAY = 1000
const SHIPPING_ESTIMATE_RETRY_MAX_DELAY = 5000
const SHIPPING_ESTIMATE_RETRY_JITTER = 500
// Only a failure that a later attempt can fix is retried: no response (network, timeout),
// 5xx or 429. A 4xx, a cancelled request or an answer without prices is final (FW-4002).
const isTransientShippingEstimateError = (error) => {
  if (!error || error.__CANCEL__) return false
  if (!error.response) return true

  const status = Number(error.response.status)
  return status === 429 || status >= 500
}
// Exponential backoff with jitter, capped; a 429 Retry-After (in seconds) wins when present.
const getShippingEstimateRetryDelay = (attempt, error, random = Math.random) => {
  const headers = (error && error.response && error.response.headers) || {}
  const retryAfter = Number(headers['retry-after']) * 1000
  const backoff = retryAfter > 0
    ? retryAfter
    : SHIPPING_ESTIMATE_RETRY_BASE_DELAY * Math.pow(2, attempt - 1)

  return Math.min(backoff, SHIPPING_ESTIMATE_RETRY_MAX_DELAY) + Math.round(random() * SHIPPING_ESTIMATE_RETRY_JITTER)
}
const waitFor = (ms) => new Promise(resolve => setTimeout(resolve, ms))
// Runs `request` until it resolves, retrying transient failures. `isStale` stops the
// retries once a newer estimate (another address) has taken over.
const requestShippingEstimate = async (request, { isStale = () => false, wait = waitFor, random } = {}) => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await request(attempt)
    } catch (error) {
      if (attempt >= SHIPPING_ESTIMATE_MAX_ATTEMPTS || isStale() || !isTransientShippingEstimateError(error)) throw error
      await wait(getShippingEstimateRetryDelay(attempt, error, random))
      if (isStale()) throw error
    }
  }
}
const shippingCourierItems = {
  'jne': { val: 'jne', label: 'JNE', label2: 'JNE', imageSrc: `/img/shippingCouriers/JNE.png` },
  'siCepat': { val: 'siCepat', label: 'SiCepat', label2: 'SiCepat', imageSrc: `/img/shippingCouriers/Sicepat.svg` },
  'sap': { val: 'sap', label: 'SAP', label2: 'SAP', imageSrc: `/img/shippingCouriers/SAP.svg` },
  'iDexpress': { val: 'iDexpress', label: 'iDexpress', label2: 'iDexpress', imageSrc: `/img/shippingCouriers/iDexpress.png` },
  'jNt': { val: 'jNt', label: 'J&T', label2: 'J&T', imageSrc: `/img/shippingCouriers/JT.png` },
  'ninja': { val: 'ninja', label: 'Ninja Xpress', label2: 'Ninja Xpress', imageSrc: `/img/shippingCouriers/ninja.png` },
  'lion': { val: 'lion', label: 'Lion Parcel', label2: 'Lion Parcel', imageSrc: `/img/shippingCouriers/lion.svg` },
  'spx': { val: 'spx', label: 'SPX Express', label2: 'SPX Express', imageSrc: `/img/shippingCouriers/spx.svg` }
}

const activeShippingCourierItems = shippingCourierTypes.reduce((items, courier) => {
  items[courier] = shippingCourierItems[courier]
  return items
}, {})

module.exports = {
  shippingCourierTypes,
  UNAVAILABLE_SHIPPING_CHECKOUT_ERROR,
  isUnavailableAutomatedShipping,
  applyNonCodShippingToCod,
  isInactiveShippingCourier,
  isDisplayableShippingEstimate,
  UNREACHABLE_SHIPPING_ADDRESS_MESSAGE,
  SHIPPING_ESTIMATE_MAX_ATTEMPTS,
  SHIPPING_ESTIMATE_TIMEOUT,
  isTransientShippingEstimateError,
  getShippingEstimateRetryDelay,
  requestShippingEstimate,
  shippingCourierTypesCargo,
  shippingCourierItems,
  activeShippingCourierItems,
  shippingCourierItemsCargo: {
    'JNECargo': { val: 'JNECargo', label: 'JNE Trucking', label2: 'JNE Trucking', imageSrc: `/img/shippingCouriers/JNE.png` },
    'SiCepatCargo': { val: 'SiCepatCargo', label: 'SiCepat GOKIL', label2: 'SiCepat GOKIL', imageSrc: `/img/shippingCouriers/Sicepat.svg` },
    'SapCargo': { val: 'SapCargo', label: 'SAP Cargo', label2: 'SAP Cargo', imageSrc: `/img/shippingCouriers/SAP.svg` },
    'iDexpressCargo': { val: 'iDexpressCargo', label: 'IDExpress Cargo', label2: 'IDExpress Cargo', imageSrc: `/img/shippingCouriers/iDexpress.png` }
  }
}
